import { useContext, useRef, useState } from "react";
import toast from "react-hot-toast";
import { EyeIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

import { LabourerContext } from "../contexts/LabourerContext";

import SectionHeader from "./SectionHeader";
import Modal from "./Modal";
import ConfirmPrompt from "./ConfirmPrompt";
import ButtonLoader from "./ButtonLoader";

import useResume from "../hooks/useResume";
import resumeService from "../services/resumeService";

const ResumeSection = () => {
  const { labourer } = useContext(LabourerContext);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [file, setFile] = useState(null);

  const fileInputRef = useRef(null);

  const { data, isLoading, mutate } = useResume({ labourerId: labourer._id });
  const resume = data?.resume;

  const handleCloseModal = () => {
    setIsUploadModalOpen(false);
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isUploading) return;

    const resumeData = new FormData();
    resumeData.append('labourer', labourer._id);
    resumeData.append('file', file);

    try {
      setIsUploading(true);

      if (resume) {
        await resumeService.updateResume({ resumeId: resume._id, resumeData });
      } else {
        await resumeService.createResume({ resumeData });
      }

      await mutate();
      setIsUploadModalOpen(false);
      setFile(null);

      toast.success('Resume uploaded');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'An error occurred while uploading the resume');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteResume = async () => {
    try {
      setIsDeleting(true);

      await resumeService.deleteResume({ resumeId: resume._id });
      await mutate();

      setIsConfirmDeleteOpen(false);
      toast.success('Resume deleted');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'An error occurred while deleting the resume');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="border-ase-blue border rounded-lg h-fit flex-grow pb-1">
        <SectionHeader
          title="Resume"
          actionsLeft={
            !isLoading && !resume && (
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className='px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs'
              >
                Upload
              </button>
            )
          }
        />

        <div className="p-4">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading...</p>
          ) : resume ? (
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Resume on file</span>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.open(resume.file, '_blank')}
                  className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white'
                >
                  <EyeIcon className='md:hidden text-white h-3 w-3' />
                  <span className='hidden md:block'>View</span>
                </button>

                <button
                  onClick={() => setIsUploadModalOpen(true)}
                  className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-ase-blue text-white'
                >
                  <PencilSquareIcon className='md:hidden text-white h-3 w-3' />
                  <span className='hidden md:block'>Update</span>
                </button>

                <button
                  onClick={() => setIsConfirmDeleteOpen(true)}
                  className='py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white'
                >
                  <TrashIcon className='md:hidden text-white h-3 w-3' />
                  <span className='hidden md:block'>Delete</span>
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No resume uploaded</p>
          )}
        </div>
      </div>

      <Modal isOpen={isUploadModalOpen}>
        <div className="bg-white rounded-lg border border-ase-blue w-full md:w-[500px]">
          <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
            <p>{resume ? 'Update Resume' : 'Upload Resume'}</p>

            <p
              onClick={handleCloseModal}
              className="font-bold cursor-pointer text-2xl"
            >
              X
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col w-full">
            <div className="p-4 flex flex-col gap-4">
              <div>
                <label
                  className="block mb-2 text-sm font-medium"
                  htmlFor="resume_file_input"
                >
                  Upload file
                </label>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => setFile(e.target.files[0])}
                  required={true}
                  id="resume_file_input"
                  className="block p-4 w-full text-sm text-white rounded-lg cursor-pointer bg-ase-blue focus:outline-none ase-upload-input"
                />
              </div>

              <button
                type="submit"
                className="ase-button-primary h-[48px] w-full flex items-center justify-center"
              >
                {isUploading ? <ButtonLoader /> : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={isConfirmDeleteOpen}>
        <ConfirmPrompt
          message="Are you sure you want to delete your resume?"
          onConfirm={handleDeleteResume}
          isLoading={isDeleting}
          onCancel={() => setIsConfirmDeleteOpen(false)}
        />
      </Modal>
    </>
  );
};

export default ResumeSection;
